import React, { useEffect, useRef, useState } from 'react';

import PriorityLinks, { CreateListingMenuLink } from './PriorityLinks';
import LinksMenu from './LinksMenu';

import css from './CustomLinksMenu.module.css';

const draftId = '00000000-0000-0000-0000-000000000000';

// Generate role-based navigation links.
// In this project, provider capability is represented by showCreateListingsLink.
const getRoleBasedLinks = (intl, isAuthenticated, isCustomer, showCreateListingsLink) => {
  const links = [];

  if (!isAuthenticated) {
    return links;
  }

  // Provider: Show Discover before Create Listing. Wishlists is the heart icon in the topbar.
  if (showCreateListingsLink) {
    links.push(
      {
        group: 'primary',
        text: intl.formatMessage({ id: 'TopbarDesktop.discover' }),
        type: 'internal',
        route: {
          name: 'DiscoverPage',
        },
      },
      {
        group: 'primary',
        text: intl.formatMessage({ id: 'TopbarDesktop.createListing' }),
        type: 'internal',
        route: {
          name: 'EditListingPage',
          params: { slug: 'draft', id: draftId, type: 'new', tab: 'details' },
        },
        highlight: true,
      }
    );
  } else if (isCustomer) {
    // Buyer-only: Discover is the only top link; Wishlists is the heart icon in the topbar.
    links.push(
      {
        group: 'primary',
        text: intl.formatMessage({ id: 'TopbarDesktop.discover' }),
        type: 'internal',
        route: {
          name: 'DiscoverPage',
        },
      }
    );
  }

  return links;
};

const createListingLinkConfigMaybe = (intl, showLink) =>
  showLink
    ? [
        {
          group: 'primary',
          text: intl.formatMessage({ id: 'TopbarDesktop.createListing' }),
          type: 'internal',
          route: {
            name: 'EditListingPage',
            params: { slug: 'draft', id: draftId, type: 'new', tab: 'details' },
          },
          highlight: true,
        },
      ]
    : [];

/**
 * Group links to 2 groups:
 * - priorityLinks (Those primary links that fit into current width of the TopbarDesktop.)
 * - menuLinks (The rest of the links that are shown inside dropdown menu.)
 *
 * @param {*} links array of link configs in an order where primary group is shown first
 * @param {*} containerWidth width reserved for the CustomLinksMenu component
 * @param {*} menuMoreWidth width that the "More" label takes
 * @returns Object containing arrays: { priorityLinks, menuLinks }
 */
const groupMeasuredLinks = (links) => {
  // Always show all links directly in the desktop topbar — no dropdown menu.
  return { priorityLinks: links, menuLinks: [] };
};

const calculateContainerWidth = (containerRefTarget, parentWidth) => {
  // Siblings include logo, search form, (inbox, profile menu || login signup)
  const siblingArray = containerRefTarget?.parentNode?.childNodes
    ? Array.from(containerRefTarget.parentNode.childNodes).filter(n => n !== containerRefTarget)
    : [];
  const siblingWidthsCombined = siblingArray.reduce((acc, node) => acc + node.offsetWidth, 0);

  // .root class of the TopbarDesktop has 24px padding on the right
  // Firefox doesn't support computedStyleMap()
  const parentStyleMap = containerRefTarget?.parentElement?.computedStyleMap
    ? containerRefTarget.parentElement.computedStyleMap()
    : null;
  const topbarPaddingRight = parentStyleMap?.get('padding-right')?.value;
  const padding = topbarPaddingRight != null ? topbarPaddingRight : 24;

  // We figure out available width from parent (TopbarDesktop/<nav>) and siblings
  const availableContainerWidth = parentWidth - siblingWidthsCombined - padding;
  return availableContainerWidth;
};

/**
 * Create CustomLinksMenu component that takes all the extra space from the TopbarDesktop and
 * renders primary links directly there if there's enough space - and secondary links are shown inside a "More" menu.
 * If the space is limited, primary links as well as secondary links are shown inside the "More" menu.
 *
 * Note: this component is inherently a bit fragile as it needs to deal with DOM directly. If you customize TopbarDesktop,
 * test the responsiveness thoroughly.
 *
 * props:
 * - customLinks: array of link configurations.
 * - hasClientSideContentReady: indicates if TopbarDesktop is ready to render
 * - currentPage: string that indicates if this is "LandingPage" or "SearchPage", etc.
 * - intl: React Intl instance
 *
 * @param {*} props contains currentPage, customLinks, intl, and hasClientSideContentReady
 * @returns component to be placed inside TopbarDesktop
 */
const CustomLinksMenu = ({
  currentPage,
  customLinks = [],
  hasClientSideContentReady,
  intl,
  showCreateListingsLink,
  isAuthenticated = false,
  isCustomer = false,
}) => {
  const containerRef = useRef(null);
  const observer = useRef(null);
  const [mounted, setMounted] = useState(false);
  const [moreLabelWidth, setMoreLabelWidth] = useState(0);
  // Use role-based links instead of just create listing link
  const roleBasedLinks = getRoleBasedLinks(
    intl,
    isAuthenticated,
    isCustomer,
    showCreateListingsLink
  );
  const isBuyerOnly = isCustomer && !showCreateListingsLink;
  const createLinksList = (roleLinks, extraLinks) =>
    isBuyerOnly ? [...roleLinks] : [...roleLinks, ...extraLinks];
  const [links, setLinks] = useState(createLinksList(roleBasedLinks, customLinks));

  const [layoutData, setLayoutData] = useState({
    priorityLinks: links,
    menuLinks: [],
    containerWidth: 0,
  });

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const nextLinks = createLinksList(roleBasedLinks, customLinks);
    const linkKey = link => {
      const routeName = link?.route?.name || '';
      const routeParams = link?.route?.params ? JSON.stringify(link.route.params) : '';
      const href = link?.href || '';
      const text = typeof link?.text === 'string' ? link.text : '';
      return `${link?.group || ''}|${link?.type || ''}|${routeName}|${routeParams}|${href}|${text}|${
        link?.highlight ? '1' : '0'
      }`;
    };
    const currentKey = links.map(linkKey).join('||');
    const nextKey = nextLinks.map(linkKey).join('||');
    if (currentKey !== nextKey) {
      setLinks(nextLinks);
    }
  }, [customLinks, roleBasedLinks, links]);

  useEffect(() => {
    let animationFrameId = null;
    const body = document.body;
    const container = containerRef.current;

    if (hasClientSideContentReady) {
      // ResizeObserver sets layout data: grouped priority links and links that go to menuLinks dropdown
      observer.current = new ResizeObserver(entries => {
        const containerRefParentWidth = container?.parentNode?.offsetWidth;
        const bodyOffsetWidth = body.offsetWidth;

        for (const entry of entries) {
          // Body's width has changed (aka viewport has changed)
          const isBodyTheTarget = entry.target === body;
          // If the width of the TopbarDesktop (aka <nav>) changes, this assumes that the document.body has the correct width.
          const hasWidthOfTopbarDesktopChanged =
            !isBodyTheTarget && containerRefParentWidth !== bodyOffsetWidth;

          if (isBodyTheTarget || hasWidthOfTopbarDesktopChanged) {
            const target = container;
            const availableContainerWidth = calculateContainerWidth(target, bodyOffsetWidth);

            // The groupedLinks variable contains { priorityLinks, menuLinks }
            const groupedLinks = groupMeasuredLinks(links);
            // The setLayoutData call affects the UI. This pushes the painting to the next frame.
            animationFrameId = window.requestAnimationFrame(() => {
              if (container) {
                setLayoutData({ ...groupedLinks, containerWidth: availableContainerWidth });
              }
            });
            // After setLayoutData is called, don't process other entries
            break;
          }
        }
      });

      if (container) {
        // We need to observe both document body and the component's own container
        // When the window is squeezed, priority links prevent the container to shrink smaller.
        // At that point, we need to calculate the width from the width of the body.
        // It's also possible that some of the other elements get a repaint after window-level repaint (e.g. image loads)
        // In those cases, we just need to
        observer.current.observe(body);
        observer.current.observe(container);
      }
    }
    return () => {
      if (body) {
        observer.current?.unobserve(body);
      }
      if (container) {
        observer.current?.unobserve(container);
      }
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [containerRef, hasClientSideContentReady, moreLabelWidth, links]);

  const { priorityLinks, menuLinks } = layoutData;

  const isMeasured = !!links?.[0]?.width;
  const hasMenuLinks = menuLinks?.length > 0;
  const hasPriorityLinks = isMeasured && priorityLinks.length > 0;

  // Nothing to lay out: signed out with no configured custom links, this container still took
  // the inline width its own ResizeObserver had measured - a 250px hole in the middle of the
  // topbar with nothing in it. Returning null after the hooks keeps their order intact, and the
  // observer simply never attaches, since its effect is guarded on containerRef.current.
  if (links.length === 0) {
    return null;
  }

  return (
    <div className={css.customLinksMenu} ref={containerRef}>
      <PriorityLinks links={links} priorityLinks={priorityLinks} setLinks={setLinks} />
      {mounted && hasMenuLinks ? (
        <LinksMenu
          id="linksMenu"
          currentPage={currentPage}
          links={menuLinks}
          showMoreLabel={hasPriorityLinks}
          moreLabelWidth={moreLabelWidth}
          setMoreLabelWidth={setMoreLabelWidth}
          intl={intl}
        />
      ) : null}
    </div>
  );
};

export default CustomLinksMenu;
